// Hybrid authentication system: 
// - Production: Uses Cloudflare Pages Functions (server-side)
// - Development: Uses direct Supabase calls (client-side)

import { createClient } from '@supabase/supabase-js'

// Initialize Supabase for development environment
let supabase = null;
// Detect Vite dev server even when accessed via LAN IP from mobile
const isDevelopment = (
    (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV === true)
    || window.location.port === '5173'
    || window.location.hostname === 'localhost'
    || window.location.hostname === '127.0.0.1'
);

if (isDevelopment) {
    // Development: Use client-side Supabase
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (supabaseUrl && supabaseKey) {
        try {
            supabase = createClient(supabaseUrl, supabaseKey);
            console.log('Development: Supabase initialized for client-side auth');
        } catch (error) {
            console.error('Failed to initialize Supabase:', error);
        }
    } else {
        console.warn('Development: Supabase environment variables not found');
    }
}

function openTab(evt, tabName) {
    var i, tabcontent, tablinks;
    
    // Hide all tab content
    tabcontent = document.getElementsByClassName("tab-content");
    for (i = 0; i < tabcontent.length; i++) {
        tabcontent[i].style.display = "none";
        tabcontent[i].style.opacity = "0";
        tabcontent[i].classList.remove("active");
    }
    
    // Remove active class from all tab links
    tablinks = document.getElementsByClassName("tab-link");
    for (i = 0; i < tablinks.length; i++) {
        tablinks[i].classList.remove("active");
    }
    
    // Animate container height to the new panel
    const panels = document.querySelector('.tab-panels');
    const selectedTab = document.getElementById(tabName);
    const currentActive = document.querySelector('.tab-content.active');

    // Measure target height first
    selectedTab.style.display = 'block';
    selectedTab.classList.add('measuring');
    selectedTab.style.opacity = '0';
    const targetHeight = selectedTab.offsetHeight;
    selectedTab.classList.remove('measuring');
    selectedTab.style.display = '';

    if (panels) {
        const startHeight = panels.offsetHeight;
        // If no height yet (first load), set it to current active or target
        const currentHeight = currentActive ? currentActive.offsetHeight : targetHeight;
        panels.style.height = currentHeight + 'px';
        // Force reflow
        void panels.offsetHeight;
        panels.style.height = targetHeight + 'px';
    }

    // Now switch active tab content
    selectedTab.style.display = "block";
    selectedTab.classList.add("active");
    setTimeout(function() {
        selectedTab.style.opacity = "1";
    }, 10);

    // Add active class to the clicked tab link
    evt.currentTarget.classList.add("active");

    // Move sliding indicator
    try {
        const tabs = document.querySelector('.tabs');
        const indicator = document.querySelector('.tab-indicator');
        const activeLink = evt.currentTarget;
        if (indicator && tabs && activeLink) {
            const tabsRect = tabs.getBoundingClientRect();
            const linkRect = activeLink.getBoundingClientRect();
            const left = linkRect.left - tabsRect.left;
            const width = linkRect.width;
            indicator.style.left = `${left}px`;
            indicator.style.width = `${width}px`;
        }
    } catch (_) {}
}

// Make openTab function globally available
window.openTab = openTab;

async function handleLogin(event) {
    event.preventDefault();
    
    const form = event.target;
    const formData = new FormData(form);
    
    // Get the active tab to determine login method
    const activeTab = document.querySelector('.tab-content.active');
    const isGolfIdLogin = activeTab && activeTab.id === 'golf-id-login';
    
    let email, password, golfGenieId;
    
    if (isGolfIdLogin) {
        golfGenieId = formData.get('golf-genie-id')?.trim().toUpperCase();
        if (!golfGenieId) {
            showNotification('Please enter your Golf Genie ID.', 'error');
            return;
        }
    } else {
        email = formData.get('email')?.trim().toLowerCase();
        password = formData.get('password');
        
        if (!email || !password) {
            showNotification('Please enter your email and password.', 'error');
            return;
        }
    }
    
    try {
        // no noisy debug notifications in production UI
        // Single path: always call server function for GGID magic link; otherwise dev/prod split
        if (golfGenieId) {
            await handleGgidMagicLink(golfGenieId);
        } else if (isDevelopment && supabase) {
            await handleDevelopmentLogin(email, password);
        } else {
            await handleProductionLogin(email, password);
        }
    } catch (error) {
        console.error('Login error:', error);
        showNotification('An error occurred during login. Please try again.', 'error');
    }
}

// Development login using client-side Supabase
async function handleDevelopmentLogin(email, password) {
    let authResult;
    
    // Regular email login in development
    authResult = await supabase.auth.signInWithPassword({
        email: email,
        password: password
    });
    
    if (authResult.error) {
        if (authResult.error.message.includes('Invalid login credentials')) {
            showNotification('Invalid email/password combination. Please try again.', 'error');
        } else {
            showNotification(authResult.error.message, 'error');
        }
        return;
    }
    
    // Check if user profile is complete
    const profileComplete = await checkProfileCompleteDev(authResult.data.user);
    
    if (!profileComplete) {
        showNotification('Please complete your profile setup.', 'info');
        window.location.assign('./complete-profile.html');
        return;
    }
    // Persist session for cross-page access as fallback on mobile
    try {
        const sessionData = authResult.data.session;
        if (sessionData && sessionData.access_token) {
            localStorage.setItem('supabase_session', JSON.stringify({
                access_token: sessionData.access_token,
                refresh_token: sessionData.refresh_token,
                user: authResult.data.user
            }));
        }
    } catch (_) {}

    showNotification('Login successful. Redirecting to dashboard...', 'success');
    window.location.assign('./dashboard.html?message=signed-in');
}

// Production login using serverless functions
async function handleProductionLogin(email, password) {
    const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password })
    });
    
    const result = await response.json();
    if (!response.ok) {
        console.error('Production login failed with status', response.status, result);
    }
    
    if (!result.success) {
        if (result.error.includes('Invalid login credentials') || result.error.includes('not found')) {
            showNotification('Invalid credentials. Please check your login information.', 'error');
        } else {
            showNotification(result.error, 'error');
        }
        return;
    }
    
    // Store session data in localStorage for client-side access
    localStorage.setItem('supabase_session', JSON.stringify({
        access_token: result.session.access_token,
        refresh_token: result.session.refresh_token,
        user: result.user
    }));
    
    if (!result.profileComplete) {
        showNotification('Please complete your profile setup.', 'info');
        window.location.assign('./complete-profile.html');
        return;
    }
    
    showNotification('Login successful. Redirecting to dashboard...', 'success');
    window.location.assign('./dashboard.html?message=signed-in');
}

// GGID Magic Link handler (server sends sign-in link to email behind the ID)
async function handleGgidMagicLink(golfGenieId) {
    try {
        const res = await fetch('/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ golfGenieId })
        });
        const result = await res.json();
        if (!res.ok || !result.success) {
            const msg = result?.error || 'Failed to send magic link.';
            showNotification(msg, 'error');
            return;
        }
        showNotification('Magic sign-in link sent! Check your email.', 'success');
    } catch (e) {
        console.error(e);
        showNotification('Failed to send magic link.', 'error');
    }
}

// Check profile completion for development
async function checkProfileCompleteDev(user) {
    try {
        const { data: userProfile, error } = await supabase
            .from('users')
            .select('id, first_name, last_name, display_name')
            .eq('id', user.id)
            .single();
        
        if (error || !userProfile) {
            return false;
        }
        
        return userProfile.first_name && userProfile.last_name && userProfile.display_name;
    } catch (error) {
        console.error('Error checking profile:', error);
        return false;
    }
}

// Custom notification system (consistent with dashboard styling)
function showNotification(message, type = 'info') {
    // Remove any existing notifications
    const existingNotifications = document.querySelectorAll('.notification, .custom-notification');
    existingNotifications.forEach(notification => notification.remove());
    
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `custom-notification ${type}`;
    notification.innerHTML = `
        <div class="notification-content">
            <span class="notification-icon">${type === 'success' ? '✓' : type === 'error' ? '⚠' : 'ℹ'}</span>
            <span class="notification-message">${message}</span>
        </div>
    `;
    
    // Add styles (matching dashboard dark green theme)
    notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: ${type === 'success' ? '#2c4c3b' : type === 'error' ? '#dc3545' : '#17a2b8'};
        color: white;
        padding: 16px 24px;
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
        z-index: 10000;
        transform: translateX(400px);
        transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        font-size: 14px;
        font-weight: 500;
        backdrop-filter: blur(10px);
        border: 1px solid ${type === 'success' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)'};
    `;

    notification.querySelector('.notification-content').style.cssText = `
        display: flex;
        align-items: center;
        gap: 12px;
    `;

    notification.querySelector('.notification-icon').style.cssText = `
        font-size: 16px;
        font-weight: bold;
    `;
    
    // Add to page
    document.body.appendChild(notification);
    
    // Animate in
    setTimeout(() => {
        notification.style.transform = 'translateX(0)';
    }, 10);
    
    // Auto dismiss after 4 seconds
    setTimeout(() => {
        notification.style.transform = 'translateX(400px)';
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, 400);
    }, 4000);
}

// Golf Genie ID input formatting
function setupGolfGenieIdInput() {
    const ggidInput = document.getElementById('ggid-input');
    if (ggidInput) {
        ggidInput.addEventListener('input', function(e) {
            let value = e.target.value;
            value = value.replace(/[^A-Za-z0-9]/g, ''); // Allow letters and numbers
            value = value.toUpperCase(); // Convert to uppercase
            value = value.substring(0, 10); // Limit to 10 characters
            e.target.value = value;
        });
        
        ggidInput.addEventListener('paste', function(e) {
            e.preventDefault();
            let paste = (e.clipboardData || window.clipboardData).getData('text');
            paste = paste.replace(/[^A-Za-z0-9]/g, '').toUpperCase().substring(0, 10);
            e.target.value = paste;
        });
    }
}


// Check for URL messages and display notifications
function checkUrlMessages() {
    const urlParams = new URLSearchParams(window.location.search);
    const message = urlParams.get('message');
    
    if (message === 'signed-out') {
        showNotification('Signed out successfully!', 'success');
        // Clean URL
        const url = new URL(window.location);
        url.searchParams.delete('message');
        window.history.replaceState({}, document.title, url);
    }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
    // Check for cross-page messages
    checkUrlMessages();
    
    // Set up Golf Genie ID input formatting
    setupGolfGenieIdInput();
    
    // Show email login tab by default
    const emailTab = document.getElementById('email-login');
    const emailTabButton = document.querySelector('.tab-link[onclick*="email-login"]');
    if (emailTab && emailTabButton) {
        emailTab.style.display = 'block';
        emailTab.classList.add('active');
        emailTab.style.opacity = '1';
        emailTabButton.classList.add('active');
        // Initialize indicator to correct size/position
        try {
            const tabs = document.querySelector('.tabs');
            const indicator = document.querySelector('.tab-indicator');
            const tabsRect = tabs.getBoundingClientRect();
            const linkRect = emailTabButton.getBoundingClientRect();
            indicator.style.left = `${linkRect.left - tabsRect.left}px`;
            indicator.style.width = `${linkRect.width}px`;
        } catch(_) {}
        // Initialize panels height to current content for smooth future transitions
        try {
            const panels = document.querySelector('.tab-panels');
            if (panels && emailTab) {
                panels.style.height = emailTab.offsetHeight + 'px';
            }
        } catch(_) {}
    }
    
    // Set up form submission for both forms
    const emailForm = document.getElementById('login-form');
    const ggidForm = document.getElementById('ggid-form');
    
    function preventDefaultIfHandled(e) {
        try {
            handleLogin(e);
        } catch (err) {
            console.error(err);
        }
    }

    if (emailForm) {
        emailForm.addEventListener('submit', function(e){ e.preventDefault(); preventDefaultIfHandled(e); });
    }
    
    if (ggidForm) {
        ggidForm.addEventListener('submit', function(e){ e.preventDefault(); preventDefaultIfHandled(e); });
    }
});